import { useCustomizeMode } from '@/features/experiences/components/CustomizeMode';
import {
  useIsCompactPlatformBar,
  useIsMobileBreakpoint,
} from '@/hooks/useIsBreakpoint';
import { usePageExperienceContext } from '../../contexts/PageExperienceContext';

type Args = {
  // Whether the active view has a date-range selector (from useDateRange). Only
  // date-range views can jump to "today", so it's threaded in rather than
  // re-derived here (it needs the resolved active child).
  hasDateRange: boolean;
  // Whether the active view paints its events with category colors (from
  // useActiveViewCategoryColors). Threaded in for the same reason, and because
  // resolving it without flicker needs state this hook has no business holding.
  hasCategoryColors: boolean;
  // Whether the Key has any category to list (from useKeyCategories). Not just
  // "are there categories": unnamed ones only count when events in view carry
  // them, which takes event data this hook has no business fetching.
  hasKeyCategories: boolean;
};

// Which platform-bar controls are visible.
//
// Visibility is driven by a few "takeover" flows that each claim the surface,
// plus responsive breakpoints. Rather than scatter the matrix across inline
// conditionals (and duplicate it in child components), it lives here in one
// place so the rules — and their interactions — are named and testable.
//
// Takeover flows (mutually exclusive; all share the `edit` URL param):
//   - customize mode  (isCustomizeModeActive) — the customize bar/sidebar
//   - add-view sub-flow (addViewMode)         — a sub-state of the Views panel
//   - Send-to-calendar (isSendMode)           — the export sidebar
//   - Magic add-content (isMagicMode)         — self-gated inside MagicButton
export type PlatformBarVisibility = {
  viewPicker: boolean;
  today: boolean;
  timezone: boolean;
  eventFilter: boolean;
  interactions: boolean;
  magicButton: boolean;
  key: boolean;
};

export const usePlatformBarVisibility = ({
  hasDateRange,
  hasCategoryColors,
  hasKeyCategories,
}: Args): PlatformBarVisibility => {
  const { addViewMode, isCustomizeModeActive, action } = useCustomizeMode();
  const { experience } = usePageExperienceContext();
  const isMobile = useIsMobileBreakpoint();
  const isCompact = useIsCompactPlatformBar();

  // The add-view / change-layout flow hides the event-filter + Send cluster so
  // its own controls own the bar.
  const isAddViewFlowActive = addViewMode !== null;

  return {
    // Switching views mid-flow would swap the surface out from under the panel asking
    // which view to add. Only that sub-flow hides it: moving between views is how you
    // customize each one, so plain customize mode keeps it.
    viewPicker: !isAddViewFlowActive,
    // Jump-to-today only exists for date-range views; dropped on mobile where
    // the left cluster already fills the narrow bar.
    today: hasDateRange && !isMobile,
    // Non-essential and nowrap; dropped once the bar gets crowded (~1100px).
    timezone: !isCompact,
    eventFilter: !isAddViewFlowActive,
    // Governed by the Schedule-settings "Add to calendar" toggle, and yielded to
    // customize mode + the add-view flow.
    interactions:
      experience.interactionApps.length > 0 &&
      !isCustomizeModeActive &&
      !isAddViewFlowActive,
    // Magic yields the surface to customize mode and the Send sidebar; on mobile
    // it moves to PlannerMobileAddButton. (MagicButton also self-hides in its own
    // add-content mode, so isMagicMode isn't repeated here.)
    magicButton: !isCustomizeModeActive && !action && !isMobile,
    // The Key names what each category color means, so it needs colors on the
    // events and at least one category worth listing. Yields to the add-view flow
    // alongside the filter.
    key: hasCategoryColors && hasKeyCategories && !isAddViewFlowActive,
  };
};
